<template>
  <v-container class="letter-of-wishes-container standard-content-container">
    <v-row no-gutters>
      <v-col cols="12" md="5">
        <v-row no-gutters class="title-section">
          <v-col
            cols="12"
            sm="2"
            class="link-container"
            :class="{
              'small-container':
                this.$vuetify.breakpoint.width <=
                this.$vuetify.breakpoint.thresholds.xs,
            }"
          >
            <router-link
              :to="{ name: 'OverviewPage', hash: '#letter-of-wishes' }"
              class="back-arrow"
              ><v-icon large color="mid-grey" class="mt-1">{{
                icons.mdiArrowLeft
              }}</v-icon></router-link
            >
          </v-col>
          <v-col cols="12" sm="10" class="standard-title-container">
            <h1>Letter of wishes</h1>
          </v-col>
        </v-row>
      </v-col>
      <v-col cols="12" md="7">
        <div class="left-section-container">
          <h3>Examples of what you can include in your letter of wishes:</h3>
          <ul>
            <li>Who to notify of your death.</li>
            <li>
              Any guiding advice for Executors and Trustees on how you would
              like your assets managed
            </li>
            <li>
              Any guiding advice to your children's guardians on how to raise
              your children - schools, religion, cultural activities, sports
              activities
            </li>
            <li>Preferences around your funeral or burial type</li>
          </ul>
          <h3 class="mt-4">
            What <span class="text-highlight">not</span> to put in your letter
            of wishes:
          </h3>
          <ul>
            <li>do not appoint guardians for your minor children</li>
            <li>do not appoint an Executor</li>
            <li>do not leave assets or money to people</li>
          </ul>
          <p class="mt-0">... these are all covered in your will.</p>
          <h4 class="mb-1">
            Note that letters of wishes are not legally binding documents, but
            serve the purpose of guiding your Executor.
          </h4>
          <ValidationObserver ref="observer">
            <v-form class="mt-12" @submit.prevent="submit">
              <TextField
                v-model="letterOfWishes"
                :label="'Last wishes'"
                :type="'textarea'"
                :rows="10"
              />
              <p v-if="!whitelabel.omDesign" class="mb-0">
                What is your burial preference?
              </p>
              <ValidationProvider
                id="burial_preference"
                v-slot="{ errors }"
                name="burial_preference"
                :rules="includeBurialWishes ? 'required' : ''"
              >
                <ListSelect
                  v-model="burialPreference"
                  :items="$options.burialPreferences"
                  :name="'burial_preference'"
                  :label="'What is your burial preference?'"
                  :errors="errors"
                />
              </ValidationProvider>
              <ValidationProvider
                v-if="burialPreference == 'OTHER'"
                id="burial_preference_other"
                v-slot="{ errors }"
                name="burial_preference_other"
                rules="required|max:256"
              >
                <TextField
                  v-model="burialPreferenceOther"
                  :label="'Other burial preference description'"
                  :type="'textarea'"
                  :errors="errors"
                />
              </ValidationProvider>

              <v-row v-if="showLivingWill" no-gutters>
                <v-col cols="12" md="6">
                  <h4 class="mt-5">Do you wish to create a living will?</h4>
                </v-col>
                <v-col cols="12" md="4">
                  <ValidationProvider
                    id="include_living_will"
                    v-slot="{ errors }"
                    name="include_living_will"
                    rules="required"
                  >
                    <RadioSelect
                      v-model="includeLivingWill"
                      :items="livingWillOptions"
                      :group="'includeLivingWill'"
                      :errors="errors"
                      @change="handleIncludeLivingWillChange"
                    />
                  </ValidationProvider>
                </v-col>
              </v-row>
              <v-row v-if="showLivingWill && includeLivingWill" no-gutters>
                <v-col cols="12" md="6">
                  <h4 class="mt-5 mb-5">
                    Do you wish to include your burial wishes in your living
                    will?
                  </h4>
                </v-col>
                <v-col cols="12" md="4">
                  <ValidationProvider
                    id="include_burial_wishes"
                    v-slot="{ errors }"
                    name="include_burial_wishes"
                    rules="required"
                  >
                    <RadioSelect
                      v-model="includeBurialWishes"
                      :items="includeBurialWishesOptions"
                      :group="'includeBurialWishes'"
                      :errors="errors"
                    />
                  </ValidationProvider>
                </v-col>
              </v-row>

              <div class="text-left mt-4 submit-standard-content-btn">
                <GradientButton
                  :title="'Next'"
                  :small="true"
                  :callout="'right'"
                  :loading="buttonLoading"
                  type="submit"
                />
              </div>
            </v-form>
          </ValidationObserver>
        </div>
      </v-col>
    </v-row>

    <ErrorSnackbar v-model="showError" :error="errorMessage" />
  </v-container>
</template>

<script>
  import { UPDATE_TESTATOR_MUTATION } from '../../constants/graphql/testators';
  import ErrorSnackbar from '../common/ErrorSnackbar.vue';
  import { formHelperMixin } from '../../mixins/form-helper-mixin';

  export default {
    name: 'LetterOfWishes',
    components: {
      ErrorSnackbar,
    },
    mixins: [formHelperMixin],
    props: {
      testator: {
        required: true,
        type: Object,
      },
    },
    data() {
      return {
        letterOfWishes: null,
        includeLivingWill: null,
        burialPreference: null,
        burialPreferenceOther: null,
        errorMessage: null,
        includeBurialWishes: null,
        livingWillOptions: [
          { text: 'Yes', value: true },
          { text: 'No', value: false },
        ],
        includeBurialWishesOptions: [
          { text: 'Yes', value: true },
          { text: 'No', value: false },
        ],
      };
    },
    burialPreferences: [
      { text: '', value: '' },
      { text: 'Burial', value: 'BURIAL' },
      { text: 'Cremation', value: 'CREMATION' },
      { text: 'Organ donor', value: 'ORGAN_DONOR' },
      { text: 'Organ donor then Burial', value: 'ORGAN_DONOR_BURIAL' },
      { text: 'Organ donor then Cremation', value: 'ORGAN_DONOR_CREMATION' },
      { text: 'Scientific research', value: 'SCIENTIFIC_RESEARCH' },
      { text: 'Other (specify in text area below)', value: 'OTHER' },
    ],
    computed: {
      showLivingWill() {
        return this.testator.willCountry != 'GB';
      },
    },
    watch: {
      burialPreference(v) {
        if (v === '') {
          this.$nextTick(() => (this.burialPreference = null));
        }
      },
    },
    created() {
      this.includeLivingWill = JSON.parse(
        JSON.stringify(this.testator.includeLivingWill)
      );
      this.letterOfWishes = JSON.parse(
        JSON.stringify(this.testator.letterOfWishes)
      );
      this.burialPreference = JSON.parse(
        JSON.stringify(this.testator.burialPreference)
      );
      this.burialPreferenceOther = JSON.parse(
        JSON.stringify(this.testator.burialPreferenceOther)
      );

      if (!!this.testator?.livingWill) {
        this.includeBurialWishes = JSON.parse(
          JSON.stringify(this.testator.livingWill.includeBurialWishes)
        );
      }
      if (!this.showLivingWill) {
        this.includeLivingWill = false;
      }
    },
    methods: {
      submit() {
        this.$refs.observer.validate().then(valid => {
          if (valid) {
            this.buttonLoading = true;
            this.updateTestator();
          } else {
            const errors = this.$refs.observer.ctx.errors;
            document
              .getElementById(
                Object.keys(errors).find(field => errors[field].length)
              )
              .scrollIntoView({ block: 'center', behavior: 'smooth' });
          }
        });
      },
      handleIncludeLivingWillChange(value) {
        if (!value) {
          this.includeBurialWishes = false;
        }
      },
      updateTestator() {
        const testatorInputDetails = {
          includeLivingWill: this.includeLivingWill,
          letterOfWishes: this.letterOfWishes,
          burialPreference: this.burialPreference,
          burialPreferenceOther: this.burialPreferenceOther,
        };
        const id = this.testatorId;

        this.$apollo
          .mutate({
            mutation: UPDATE_TESTATOR_MUTATION,
            variables: {
              id,
              testatorInputDetails,
            },
          })
          .then(_response => {
            if (
              !this.includeLivingWill &&
              this.testator?.livingWill?.decisionMakerNotify
            ) {
              dataLayer.push({
                event: 'living_will_person_notified',
                omuserid: this.$store.getters?.userId,
                omtid: this.$store.getters?.omtId,
              });
            }
            this.redirectToNext();
            this.buttonLoading = false;
          })
          .catch(error => {
            if (error.message.includes('GraphQL error:')) {
              this.errorMessage = error.message
                .replace('GraphQL error:', '')
                .trim();
              this.displayError();
            } else {
              this.displayError();
              throw error;
            }
          });
      },
      redirectToNext() {
        if (this.includeLivingWill == false) {
          this.$router.push({
            name: 'OverviewPage',
            hash: '#letter-of-wishes',
          });
        } else {
          this.$router.push({
            name: 'LivingWill',
            query: {
              includeBurialWishes: this.includeBurialWishes,
            },
          });
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .letter-of-wishes-container {
    padding-top: 40px;

    .letter-image-xs {
      max-width: 48%;
      margin-top: 20px;
    }

    .burial-preference {
      max-width: 350px;
    }
    .header-row {
      padding-bottom: 40px;
      display: flex;
      gap: 76px;
      h1 {
        flex: 1;
      }
      .your-people-description {
        max-width: 331px;
        font-size: 16px;
        font-weight: 400;
        line-height: 22px;
        text-align: left;
        color: #282828;
        padding-top: 20px;
        max-width: 331px;
      }
    }
  }
</style>
